'use client'

import React, { useCallback } from 'react'

import { Check, ChevronsUpDown, LoaderCircleIcon } from 'lucide-react'
import { useTranslations } from 'next-intl'
import { parseAsString, useQueryState } from 'nuqs'

import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover'

import { cn, isFieldInvalid } from '@/utils'

import { Chip } from './chip'
import { FieldWrapper } from './field-wrapper'
import { useFieldContext } from './form'

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type AutocompleteProps<T extends { [k: string]: any }> = {
  asyncSearch?: boolean
  avatarProps?: { srcKey?: keyof T } & CustomAvatarProps
  className?: string
  data: T[]
  fullWidth?: boolean
  isCountry?: boolean
  isDisabled?: boolean
  isLoading?: boolean
  label?: string
  labelPlacement?: 'inside' | 'outside'
  multiple?: boolean
  noResultsMessage?: string
  onSelectionChange?: (keys: string | string[]) => void
  onSelectItem?: (item: T | undefined) => void
  placeholder?: string
  renderOption?: (item: T) => React.ReactNode
  renderSelected?: (item: T) => React.ReactNode
  required?: boolean
  searchParam?: string
  valueKey: keyof T
  withAvatar?: boolean
  withChips?: boolean
} & (
  | { labelKey: keyof T; labelKeys?: never }
  | { labelKey?: never; labelKeys: Array<keyof T> }
)

type CustomAvatarProps = {
  className?: string
  fallback?: React.ReactNode
  src?: string
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const Autocomplete = <T extends { [k: string]: any }>({
  asyncSearch = false,
  avatarProps,
  className,
  data,
  isCountry = false,
  isLoading,
  label,
  labelKey,
  labelKeys,
  multiple = false,
  noResultsMessage,
  onSelectionChange,
  onSelectItem,
  placeholder,
  renderOption,
  renderSelected,
  required,
  searchParam = 'search-options',
  valueKey,
  withAvatar = false,
  withChips = false,
  ...props
}: AutocompleteProps<T>) => {
  const t = useTranslations('components.form.autocomplete')

  const field = useFieldContext<string | string[]>()
  const isInvalid = isFieldInvalid(field)
  const { srcKey, ...restAvatarProps } = avatarProps || {}
  const [search, setSearch] = useQueryState(
    searchParam,
    parseAsString.withDefault('')
  )

  const [open, setOpen] = React.useState(false)

  const getLabel = useCallback(
    (item: T): string => {
      if (labelKeys) {
        return labelKeys.map((labelKey) => item[labelKey]).join(' ')
      }
      if (labelKey) {
        return item[labelKey] as string
      }
      return ''
    },
    [labelKeys, labelKey]
  )

  const selectedValue = field.state.value

  const handleSelect = (currentValue: string, item: T) => {
    if (multiple) {
      const currentSelection = Array.isArray(selectedValue) ? selectedValue : []
      const isRemoving = currentSelection.includes(currentValue)
      let newSelection
      if (isRemoving) {
        newSelection = currentSelection.filter((val) => val !== currentValue)
      } else {
        newSelection = [...currentSelection, currentValue]
      }
      field.handleChange(newSelection)
      onSelectionChange?.(newSelection)
      onSelectItem?.(isRemoving ? undefined : item)
    } else {
      const isClearing = currentValue === selectedValue
      const newValue = isClearing ? '' : currentValue
      field.handleChange(newValue)
      onSelectionChange?.(newValue)
      onSelectItem?.(isClearing ? undefined : item)
      setOpen(false)
    }
  }

  const selectedItem = React.useMemo(() => {
    if (!multiple && selectedValue) {
      return data.find((item) => item[valueKey]?.toString() === selectedValue)
    }
    return null
  }, [selectedValue, data, valueKey, multiple])

  const displayLabel = React.useMemo(() => {
    if (multiple) {
      if (Array.isArray(selectedValue) && selectedValue.length > 0) {
        return `${selectedValue.length} ${t('selected')}`
      }
    } else if (selectedItem) {
      if (renderSelected) {
        return renderSelected(selectedItem)
      }
      return getLabel(selectedItem)
    }
    const defaultPlaceholder = placeholder ?? t('placeholder')
    return defaultPlaceholder
  }, [
    selectedValue,
    selectedItem,
    getLabel,
    multiple,
    placeholder,
    renderSelected,
    t
  ])

  return (
    <FieldWrapper field={field} label={label} required={required}>
      <Popover modal={false} onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <Button
            aria-expanded={open}
            className={cn(
              'h-[42px] w-full rounded-xl *:w-full *:justify-between',
              isInvalid &&
                'border-destructive text-destructive focus-visible:ring-destructive',
              className
            )}
            data-invalid={isInvalid || undefined}
            disabled={isLoading || props.isDisabled}
            role="combobox"
            variant="outline"
          >
            {displayLabel}
            {isLoading && <LoaderCircleIcon className="h-4 w-4 animate-spin" />}
            {!isLoading && (
              <ChevronsUpDown className="ms-2 h-4 w-4 shrink-0 opacity-50" />
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="start"
          className="w-[--radix-popover-trigger-width] p-0"
        >
          <Command>
            <CommandInput
              className="h-9"
              onValueChange={asyncSearch ? setSearch : undefined}
              placeholder={t('searchPlaceholder')}
              value={asyncSearch ? search : undefined}
            />
            <CommandList className="max-h-[300px] overflow-y-auto">
              {isLoading && (
                <div className="flex justify-center gap-1 py-6 text-sm">
                  <LoaderCircleIcon className="animate-spin" />
                  {t('loading')}
                </div>
              )}
              {!isLoading && (
                <CommandEmpty className="mx-auto max-w-xs px-4 py-2 text-center text-sm">
                  {noResultsMessage || t('noResults')}
                </CommandEmpty>
              )}
              <CommandGroup>
                {data.map((item) => {
                  const itemId = item[valueKey]?.toString()
                  const itemLabel = getLabel(item)
                  const isSelected = multiple
                    ? Array.isArray(selectedValue) &&
                      selectedValue.includes(itemId)
                    : selectedValue === itemId

                  return (
                    <CommandItem
                      disabled={
                        multiple &&
                        Array.isArray(selectedValue) &&
                        selectedValue.includes(itemId)
                      }
                      key={itemId}
                      onSelect={() => handleSelect(itemId, item)}
                      value={itemLabel}
                    >
                      <div className="flex flex-1 items-center gap-2">
                        {renderOption ? (
                          renderOption(item)
                        ) : (
                          <>
                            {withAvatar && !isCountry && (
                              <Avatar className={restAvatarProps.className}>
                                <AvatarImage
                                  alt={itemLabel}
                                  src={
                                    srcKey
                                      ? !!item[srcKey]
                                        ? (item[srcKey] as string)
                                        : '/assets/images/logo-placeholder.jpg'
                                      : undefined
                                  }
                                />
                                <AvatarFallback>
                                  {restAvatarProps.fallback ||
                                    itemLabel.charAt(0).toUpperCase()}
                                </AvatarFallback>
                              </Avatar>
                            )}
                            <span className="text-small">{itemLabel}</span>
                          </>
                        )}
                      </div>
                      <Check
                        className={cn(
                          'ms-auto h-4 w-4',
                          isSelected ? 'opacity-100' : 'opacity-0'
                        )}
                      />
                    </CommandItem>
                  )
                })}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {withChips && multiple && (
        <div className="flex flex-wrap gap-2">
          {Array.isArray(selectedValue) &&
            selectedValue.map((val) => {
              const item = data.find(
                (dataItem) => dataItem[valueKey]?.toString() === val
              )
              if (!item) return null
              const handleClose = () => {
                const newSelection = (selectedValue as string[])?.filter(
                  (v) => v !== val
                )
                field.handleChange(newSelection)
                onSelectionChange?.(newSelection)
              }
              const itemLabel = getLabel(item)
              return (
                <Chip
                  classNames={{
                    content: 'flex items-center gap-2 rtl:flex-row-reverse'
                  }}
                  handleClose={handleClose}
                  key={val}
                  label={itemLabel}
                  onClose={handleClose}
                  radius="full"
                  variant="bordered"
                  withCloseButton
                >
                  {withAvatar && !isCountry && (
                    <Avatar className={restAvatarProps.className}>
                      <AvatarImage
                        alt={itemLabel}
                        src={
                          srcKey
                            ? !!item[srcKey]
                              ? (item[srcKey] as string)
                              : '/assets/images/logo-placeholder.jpg'
                            : undefined
                        }
                      />
                      <AvatarFallback>
                        {restAvatarProps.fallback ||
                          itemLabel.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                  )}

                  {itemLabel}
                </Chip>
              )
            })}
        </div>
      )}
    </FieldWrapper>
  )
}
